interface ErrorFormatterConfig {
  maxStackLines: number;
  showFullStackByDefault: boolean;
  cleanInternalFrames: boolean;
  showTimestamp: boolean;
}

const DEFAULT_CONFIG: ErrorFormatterConfig = {
  maxStackLines: 3,
  showFullStackByDefault: false,
  cleanInternalFrames: true,
  showTimestamp: true,
};

// Patterns to identify and remove irrelevant stack frames
const INTERNAL_FRAME_PATTERNS = [
  /node_modules/,
  /webpack-internal/,
  /_next\/static/,
  /\.next\//,
  /next\/dist/,
  /react-dom/,
  /scheduler/,
  /react-reconciler/,
  /at Object\./,
  /at __webpack_require__/,
  /at webpack_require/,
  /at Object\.eval/,
  /at eval/,
  /Module\._compile/,
  /Object\.<anonymous>/,
  /at processTicksAndRejections/,
  /at async/,
];

/**
 * Clean stack trace by removing internal framework frames
 */
export function cleanStack(
  stack: string,
  config: Partial<ErrorFormatterConfig> = {}
): string {
  const { cleanInternalFrames } = { ...DEFAULT_CONFIG, ...config };

  if (!cleanInternalFrames || !stack) return stack;

  const lines = stack.split('\n');
  const headerLine = lines[0]; // Keep the error message line
  const stackLines = lines.slice(1);

  const cleanedLines = stackLines.filter((line) => {
    // Keep lines that don't match any internal patterns
    return !INTERNAL_FRAME_PATTERNS.some((pattern) => pattern.test(line));
  });

  // If we filtered out everything, keep at least the first few original lines
  const finalLines =
    cleanedLines.length > 0 ? cleanedLines : stackLines.slice(0, 3);

  return [headerLine, ...finalLines].join('\n');
}

/**
 * Truncate stack trace to specified number of lines
 */
export function truncateStack(
  stack: string,
  maxLines: number = DEFAULT_CONFIG.maxStackLines
): string {
  if (!stack) return stack;

  const lines = stack.split('\n');
  if (lines.length <= maxLines + 1) return stack; // +1 for error message line

  const headerLine = lines[0]; // Error message
  const truncatedStackLines = lines.slice(1, maxLines + 1);
  const remainingCount = lines.length - maxLines - 1;

  const truncatedLines = [
    headerLine,
    ...truncatedStackLines,
    `    ... and ${remainingCount} more lines (use facade.logger.showStacks(true) to see full stack)`,
  ];

  return truncatedLines.join('\n');
}

/**
 * Format error with cleaned and truncated stack
 */
export function formatError(
  error: Error | string,
  config: Partial<ErrorFormatterConfig> = {}
): { message: string; stack: string; fullStack: string } {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  // Handle string errors
  if (typeof error === 'string') {
    return {
      message: error,
      stack: error,
      fullStack: error,
    };
  }

  const message = error.message || 'Unknown error';
  const originalStack = error.stack || `Error: ${message}`;

  // Clean the stack trace
  const cleanedStack = cleanStack(originalStack, finalConfig);

  // Truncate if needed
  const formattedStack = finalConfig.showFullStackByDefault
    ? cleanedStack
    : truncateStack(cleanedStack, finalConfig.maxStackLines);

  return {
    message,
    stack: formattedStack,
    fullStack: cleanedStack,
  };
}

/**
 * Format error for console logging
 */
export function formatErrorForConsole(
  error: Error | string,
  config: Partial<ErrorFormatterConfig> = {}
): string {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const { message, stack } = formatError(error, finalConfig);

  const timestamp = finalConfig.showTimestamp
    ? `[${new Date().toISOString().slice(11, 23)}] `
    : '';

  return `${timestamp}${message}\n${stack}`;
}

/**
 * Create a user-friendly error display component data
 */
export function formatErrorForDisplay(
  error: Error | string,
  config: Partial<ErrorFormatterConfig> = {}
): {
  title: string;
  message: string;
  shortStack: string;
  fullStack: string;
  canShowFull: boolean;
} {
  const { message, stack, fullStack } = formatError(error, config);

  // Extract just the error message without "Error:" prefix
  const cleanMessage = message.replace(/^Error:\s*/, '');

  // Check if we have more details in full stack
  const canShowFull =
    fullStack !== stack &&
    fullStack.split('\n').length > stack.split('\n').length;

  return {
    title: 'An error occurred',
    message: cleanMessage,
    shortStack: stack,
    fullStack: fullStack,
    canShowFull,
  };
}

/**
 * Create error formatter with custom configuration
 */
export function createErrorFormatter(config: Partial<ErrorFormatterConfig>) {
  return {
    format: (error: Error | string) => formatError(error, config),
    formatForConsole: (error: Error | string) =>
      formatErrorForConsole(error, config),
    formatForDisplay: (error: Error | string) =>
      formatErrorForDisplay(error, config),
    clean: (stack: string) => cleanStack(stack, config),
    truncate: (stack: string, maxLines?: number) =>
      truncateStack(
        stack,
        maxLines ?? config.maxStackLines ?? DEFAULT_CONFIG.maxStackLines
      ),
  };
}

// Pre-configured formatters
export const developmentFormatter = createErrorFormatter({
  maxStackLines: 5,
  showFullStackByDefault: false,
  cleanInternalFrames: true,
  showTimestamp: true,
});

export const productionFormatter = createErrorFormatter({
  maxStackLines: 2,
  showFullStackByDefault: false,
  cleanInternalFrames: true,
  showTimestamp: false,
});

export const debugFormatter = createErrorFormatter({
  maxStackLines: 20,
  showFullStackByDefault: true,
  cleanInternalFrames: false,
  showTimestamp: true,
});

// Default formatter based on environment
export const defaultFormatter =
  process.env.NODE_ENV === 'production'
    ? productionFormatter
    : developmentFormatter;

// Helper function to get error details
export function getErrorDetails(error: unknown): {
  message: string;
  stack?: string;
} {
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack };
  }

  if (typeof error === 'string') {
    return { message: error };
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return {
      message: String(error.message),
      stack: 'stack' in error ? String(error.stack) : undefined,
    };
  }

  return { message: 'Unknown error occurred' };
}
